import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Flex } from 'reflexbox';

import { Snackbar, TableCell } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import useLocationQuery from '../../../../hooks/useQuery';
import Spinner from '../../../Shared/Spinner';
import TableContainer from '../../../Shared/TableContainer';
import HealtheraTable from '../../../Table/Table';
import DashboardSelect from '../DashboardSelect';
import prepareData from './prepareData';
import { getDashboardOrdersCounts, getDashboardOrders } from '../../../../redux/dashboard';
import OrderCountPerPharmacy from './components/OrderCountPerPharmacy';
import AssignModal from './components/AssignModal';
import { PendingOrdersList, PharmacyRowDetails } from './components/OrderCountPerPharmacy/styles';
import PharmacyName from '../PharmacyNotes/PharmacyName';
import ordersApi from '../../../../apis/orders';
import MaterialModal from '../../../MaterialModal/MaterialModal';
import OrderNotes from './components/OrderNotes';
import errorRoutine from '../../../../utils/errorRoutine';
import { useLoadingItems } from '../../../../hooks/useLoadingItems';
import { SearchContainer } from '../../../../healtheraComponents';
import SearchField from '../../../Search/SearchField';

const DashboardTable = () => {
  const dispatch = useDispatch();
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [orderToAssign, setOrderToAssign] = useState(null);
  const [orderForNotes, setOrderForNotes] = useState(null);
  const [noteView, setNoteView] = useState(null);
  const { query, changeLocationQuery } = useLocationQuery();
  const { status = 'unassigned' } = query;
  const { data, error, fetching, ordersCountPerPharmacy, fetchingOrdersCountPerPharmacy } = useSelector(
    (state) => state.dashboard
  );
  const errorText = errorMessage || error;
  const { data: fetchedOrders, pagination } = data || {};

  const [orders, setOrders] = useState(fetchedOrders);
  const { loadingItems, addLoadingItem, removeLoadingItem } = useLoadingItems();

  const handleCloseErrorSnackBar = () => {
    setErrorSnackbarOpen(false);
    setErrorMessage(null);
  };

  useEffect(() => setOrders(fetchedOrders), [fetchedOrders]);

  const handleRowsPerPageChange = useCallback(
    ({ target: { value } }) => {
      changeLocationQuery({
        ...query,
        limit: value,
        page: 0,
      });
    },
    [changeLocationQuery, query]
  );

  useEffect(() => {
    if (errorText) {
      setErrorSnackbarOpen(true);
    }
  }, [errorText]);

  useEffect(() => {
    dispatch(getDashboardOrdersCounts({ type: query.type, search: query.search }));
  }, [query.type, query.search, dispatch]);

  useEffect(() => {
    if (status === 'unassigned' && query.pharmacy_id) {
      const { pharmacy_id, ...restQuery } = query;
      changeLocationQuery(restQuery);
    }
  }, [status, query, changeLocationQuery]);

  useEffect(() => {
    if (status !== 'unassigned' && query.pharmacy_id) dispatch(getDashboardOrders({ limit: 100, ...query }));
  }, [status, query, dispatch]);

  const handleChangePage = (_, pageNumber) => {
    changeLocationQuery({
      ...query,
      page: pageNumber,
    });
  };

  const renderPharmacyGroupItem = (row) => (
    <PharmacyName pharmacyName={row?.pharmacy_name} pharmacyId={row?.pharmacy_id} />
  );

  const handleAssignAction = (order) => {
    addLoadingItem(order._id);
    setOrderToAssign(order);
  };

  const handleCompleteReassignment = (updatedOrder) => {
    if (query.pharmacy_id || status === 'unassigned') {
      setOrders(orders.filter((order) => order._id !== updatedOrder._id));
    } else {
      setOrders(
        orders.map((order) =>
          order._id === updatedOrder._id ? { ...order, ...updatedOrder, patient: order.patient } : order
        )
      );
    }
  };

  const handleCloseAssignModal = () => {
    removeLoadingItem(orderToAssign._id);
    setOrderToAssign(null);
  };

  const handleReportIssue = async (order) => {
    try {
      addLoadingItem(order._id);
      const [updatedOrder] = await ordersApi.reportOrRemoveIssue(order._id, !order.issue_reported);
      removeLoadingItem(order._id);

      const updatedOrders = orders.map((item) =>
        item._id === order._id ? { ...item, ...updatedOrder, patient: item.patient } : item
      );

      setOrders(updatedOrders);
    } catch (error) {
      setErrorMessage(error?.error || error?.message || 'Can not perform action. please try again.');
    }
  };

  const handleAddNote = (order) => {
    setNoteView('add_note');
    setOrderForNotes(order);
  };

  const handleCancel = async (order) => {
    try {
      if (window.confirm(`Do you want to cancel an order from ${order.patient_name}?`)) {
        addLoadingItem(order._id);
        await ordersApi.cancelOrder(order._id);
        removeLoadingItem(order._id);
        const updatedOrders = orders.filter((item) => item._id !== order._id);
        setOrders(updatedOrders);
      }
    } catch (error) {
      setOrders(order);
      errorRoutine(error, setErrorMessage);
    }
  };

  const showNotesList = (order) => {
    setNoteView('list');
    setOrderForNotes(order);
  };

  const handleCloseNoteModal = () => {
    setNoteView(null);
    setOrderForNotes(null);
  };

  function onPharmacySelect(pharmacyId) {
    const { pharmacy_id, ...restQuery } = query;
    changeLocationQuery({
      ...restQuery,
      ...(pharmacyId && { pharmacy_id: pharmacyId }),
    });
  }

  let tableContent = null;
  const pharmacyStats =
    query.pharmacy_id &&
    ordersCountPerPharmacy &&
    ordersCountPerPharmacy.find((pharmacyStats) => pharmacyStats._id === query.pharmacy_id);

  if (fetching || fetchingOrdersCountPerPharmacy) {
    tableContent = <Spinner />;
  } else if (ordersCountPerPharmacy) {
    if (query.pharmacy_id) {
      tableContent = (
        <PendingOrdersList>
          <PharmacyRowDetails onClick={() => onPharmacySelect()}>
            <ChevronLeftIcon />
            {pharmacyStats?.pharmacy_name || 'Not assigned to any pharmacy'}
          </PharmacyRowDetails>
          <HealtheraTable
            data={prepareData({
              orders,
              query,
              handleReportIssue,
              handleAddNote,
              showNotesList,
              handleCancel,
              updatingOrders: loadingItems,
              handleAssign: handleAssignAction,
            })}
            displayPagination
            totalCount={pagination?.total || 0}
            page={+query.page || 0}
            rowsPerPage={+query.limit || 100}
            onRowsPerPageChange={handleRowsPerPageChange}
            onChangePage={handleChangePage}
            isLiveDashboard={true}
          />
        </PendingOrdersList>
      );
    } else {
      tableContent = <OrderCountPerPharmacy items={ordersCountPerPharmacy} onSelect={onPharmacySelect} />;
    }
  } else if (orders) {
    tableContent = (
      <HealtheraTable
        data={prepareData({
          orders,
          query,
          updatingOrders: loadingItems,
          handleReportIssue,
          handleAddNote,
          showNotesList,
          handleCancel,
          handleAssign: handleAssignAction,
        })}
        displayPagination
        totalCount={pagination?.total}
        page={+query.page || 0}
        rowsPerPage={+query.limit || 25}
        onRowsPerPageChange={handleRowsPerPageChange}
        onChangePage={handleChangePage}
        isLiveDashboard={true}
        groupBy="pharmacy_id"
        groupTitle="pharmacy_name"
        renderGroupItem={renderPharmacyGroupItem}
        groupActionField="lastPharmacyNote"
        groupActionIndex={3}
        group
      />
    );
  }

  return (
    <TableContainer>
      <DashboardSelect />
      <SearchContainer>
        <Flex w={`100%`}>
          <SearchField onSearch={(term) => changeLocationQuery({ ...query, search: term, page: 0 })} withClear />
        </Flex>
      </SearchContainer>
      {tableContent}
      <Snackbar
        open={errorSnackbarOpen}
        onClose={handleCloseErrorSnackBar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={4000}
        message={errorText}
      />
      {orderToAssign && (
        <AssignModal
          open={!!orderToAssign}
          onClose={handleCloseAssignModal}
          order={orderToAssign}
          onComplete={handleCompleteReassignment}
        />
      )}
      {orderForNotes && noteView && (
        <MaterialModal open={!!orderForNotes} handleClose={handleCloseNoteModal}>
          <OrderNotes order={orderForNotes} defaultView={noteView} />
        </MaterialModal>
      )}
    </TableContainer>
  );
};

export const TableCellStyled = styled(TableCell)`
  padding: 0 5px;
  width: auto;
`;

export default DashboardTable;
